import axios from "axios"

const CHUNK_SIZE = 1024 * 1024

// ---------------------------------------------------
// GET TOKEN
// ---------------------------------------------------
const getToken = () => {

    let user = JSON.parse(
        localStorage.getItem("auth") || '{}'
    )

    if (user?.token) {

        return {
            headers: {
                Authorization: `Bearer ${user.token}`
            }
        }

    }

    return {}

}

// ---------------------------------------------------
// WAIT FOR INTERNET
// ---------------------------------------------------
function waitForInternet() {

    return new Promise((resolve) => {

        if (navigator.onLine) {
            resolve()
            return
        }

        const handleOnline = () => {

            window.removeEventListener(
                "online",
                handleOnline
            )

            resolve()

        }

        window.addEventListener(
            "online",
            handleOnline
        )

    })

}

// ---------------------------------------------------
// RETRY REQUEST
// ---------------------------------------------------
async function retryRequest(
    fn,
    retries = Infinity
) {

    let attempt = 0

    while (attempt < retries) {

        try {

            return await fn()

        } catch (error) {

            console.log(
                "NETWORK ERROR:",
                error
            )

            if (
                error?.message === "Network Error" ||
                error?.code === "ERR_NETWORK" ||
                error?.code === "ERR_INTERNET_DISCONNECTED"
            ) {

                console.log(
                    "Internet disconnected. Waiting..."
                )

                await waitForInternet()

                console.log(
                    "Internet restored. Retrying..."
                )

                attempt++

                continue

            }

            throw error

        }

    }

}

// ---------------------------------------------------
// GENERATE HASH
// ---------------------------------------------------
export async function generateHash(
    file,
    onHashProgress = () => { }
) {

    // loading state start
    onHashProgress({
        loading: true,
        progress: 0
    })

    const buffer =
        await file.arrayBuffer()

    // fake smooth progress animation
    // keeps UI responsive during hashing
    let fakeProgress = 0

    const progressTimer =
        setInterval(() => {

            fakeProgress += 5

            if (fakeProgress >= 90) {
                fakeProgress = 90
            }

            onHashProgress({
                loading: true,
                progress: fakeProgress
            })

        }, 100)

    const hashBuffer =
        await crypto.subtle.digest(
            "SHA-256",
            buffer
        )

    clearInterval(progressTimer)

    const hashArray = Array.from(
        new Uint8Array(hashBuffer)
    )

    onHashProgress({
        loading: false,
        progress: 100
    })

    return hashArray
        .map(b =>
            b.toString(16).padStart(2, "0")
        )
        .join("")

}

// ---------------------------------------------------
// UPLOAD FILE
// ---------------------------------------------------
export async function uploadFile(
    file,
    updateProgress,
    commit = {}
) {
    try {
        // ---------------------------------------------------
        // HASH FILE
        // ---------------------------------------------------
        const hash = await generateHash(
            file,
            updateProgress
        );

        // ---------------------------------------------------
        // CHECK FILE
        // ---------------------------------------------------
        try {
            const check = await retryRequest(() =>
                axios.post(
                    "https://repo.plakal.in/api/check-file/",
                    {
                        hash,
                        ...commit
                    },
                    getToken()
                )
            );

            if (check?.data?.exists || check?.data?.status === "no_change") {
                const msg = check?.data?.message || "File already exists with identical content. No changes detected.";
                updateProgress({
                    progress: 100,
                    speed: 0,
                    remaining: 0,
                    loading: false,
                    error: true,
                    message: msg
                });
                return check.data;
            }
        } catch (checkErr) {
            console.warn("check-file check skipped:", checkErr);
        }

        // ---------------------------------------------------
        // TOTAL CHUNKS & UNIQUE SESSION
        // ---------------------------------------------------
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const startTime = Date.now();
        const sessionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : (Date.now() + "_" + Math.random().toString(36).substring(2, 9));

        let finalResponse = null;

        // ---------------------------------------------------
        // LOOP CHUNKS
        // ---------------------------------------------------
        for (let i = 0; i < totalChunks; i++) {
            const start = i * CHUNK_SIZE;
            const chunk = file.slice(start, start + CHUNK_SIZE);

            const formData = new FormData();
            formData.append("session_id", sessionId);
            formData.append("file", chunk);
            formData.append("chunkIndex", i);
            formData.append("totalChunks", totalChunks);
            formData.append("filename", file.name);
            formData.append("hash", hash);

            Object.keys(commit).forEach((com) => {
                if (commit[com] !== undefined && commit[com] !== null) {
                    formData.append(com, commit[com]);
                }
            });

            let lastUpdate = 0;

            const upload = await retryRequest(() =>
                axios.post(
                    "https://repo.plakal.in/api/upload-chunk/",
                    formData,
                    {
                        ...getToken(),
                        onUploadProgress: (progressEvent) => {
                            const now = Date.now();
                            if (now - lastUpdate < 200) return;
                            lastUpdate = now;

                            const chunkProgress = progressEvent.total
                                ? (progressEvent.loaded / progressEvent.total)
                                : 0;
                            const uploadedBytes = (i * CHUNK_SIZE) + (chunkProgress * chunk.size);
                            const percent = Math.min(100, Math.round((uploadedBytes / file.size) * 100));
                            const elapsed = (Date.now() - startTime) / 1000;
                            const speed = elapsed > 0 ? (uploadedBytes / elapsed) : 0;
                            const remaining = speed > 0 ? (file.size - uploadedBytes) / speed : 0;

                            updateProgress({
                                progress: percent,
                                speed: speed,
                                remaining: remaining,
                                loading: false
                            });
                        }
                    }
                )
            );

            finalResponse = upload?.data;

            if (upload?.data?.status === 'upload_complete') {
                updateProgress({
                    progress: 100,
                    speed: 0,
                    remaining: 0,
                    loading: false,
                    success: true
                });
                return upload.data;
            }
        }

        // ---------------------------------------------------
        // FINAL COMPLETE
        // ---------------------------------------------------
        updateProgress({
            progress: 100,
            speed: 0,
            remaining: 0,
            loading: false,
            success: true
        });

        return finalResponse || { status: "upload_complete" };

    } catch (error) {
        console.error("RESPONSE ERROR", error?.response?.data?.error || error);
        const errMsg = error?.response?.data?.error || error?.message || "Upload failed";
        updateProgress({
            progress: 0,
            speed: 0,
            remaining: 0,
            loading: false,
            error: true,
            message: errMsg
        });
        return errMsg;
    }
}